import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { useState } from 'react';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBUserThunks, DBWorkspaceUserThunks } from '@common-reducers/DBServiceThunks';
import { getEntity } from '@common-reducers/backend-api/backend-api';
import { isEmail } from 'validator';

interface UpdateEmailActionProps {
    userId: string;
    currentEmail?: string;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onError: (message: string) => void;
}

export function UpdateEmailAction({ userId, currentEmail, open, onClose, onSuccess, onError }: UpdateEmailActionProps) {
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const dispatch = useAppDispatch();

    const handleClose = () => {
        setEmail('');
        setEmailError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!isEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        try {
            const user: any = await dispatch(getEntity.initiate({
                service: 'users',
                _id: userId
            })).unwrap();
            const currentWorkspaceId = user?.currentWorkspace?._id || user?.currentWorkspace;

            const workspaceUsersRes: any = await dispatch(DBWorkspaceUserThunks.find({
                workspaceId: currentWorkspaceId,
                userId,
                $limit: 1
            })).unwrap();

            const workspaceUsers = Array.isArray(workspaceUsersRes) ? workspaceUsersRes : (workspaceUsersRes?.data || []);
            const workspaceUser = workspaceUsers[0];

            if (!workspaceUser) {
                onError('Workspace user not found');
                return;
            }

            const userPatchRes: any = await dispatch(DBUserThunks.patch({
                entity: {
                    _id: userId,
                    email
                }
            }));

            const userPatchCode = userPatchRes?.payload?.code;
            const userPatchName = userPatchRes?.payload?.name;
            if (userPatchRes?.error || userPatchCode === 409 || userPatchName === 'Conflict') {
                setEmailError('Email already exists');
                return;
            }

            await dispatch(DBWorkspaceUserThunks.patch({
                entity: {
                    _id: workspaceUser._id,
                    email
                }
            }));
            onSuccess();
            handleClose();
        } catch (error) {
            onError('Failed to update email');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Update Email</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                    }}
                    error={!!emailError}
                    helperText={emailError}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">Update</Button>
            </DialogActions>
        </Dialog>
    );
} 
